import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/AIResponseCard.css';
import { eventBus, AIEventType, AIResponseData, AIStreamData, AIWorkflowProgressData, AIProcessingStageData } from '../lib/eventBus';
import { parseCompleteMessage, ParsedMessage ,getMessageFromStr} from '../utils/parseMessageJson';
import ChatComponentRenderer from './ChatComponentRegistry';
import LoadingText from './LoadingText';
import useTTS from '../hooks/useTTS';

// 用户问题样式 - 卡片内顶部
const userQuestionStyles: React.CSSProperties = {
  padding: '16px 20px',
  fontSize: '16px',
  fontWeight: '600',
  color: 'rgba(0, 0, 0, 0.8)',
  textAlign: 'left',
  borderRadius: '20px 20px 0 0',
};

// AI卡片样式 - 动态背景
const cardStyles: React.CSSProperties = {
  position: 'fixed',
  top: '10px',
  left: '5%',
  width: '90%',
  maxWidth: '400px',
  background: `
    linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 100%),
    linear-gradient(45deg, rgba(74, 144, 226, 0.1) 0%, rgba(80, 200, 120, 0.1) 50%, rgba(255, 107, 129, 0.1) 100%)
  `,
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
  overflow: 'hidden',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 'calc(100vh - 30px)',
};

// 卡片内容样式
const cardContentStyles: React.CSSProperties = {
  padding: '20px',
  overflowY: 'auto',
  flex: 1,
  color: 'rgba(0, 0, 0, 0.8)',
};

// 组件容器样式
const componentContainerStyles: React.CSSProperties = {
  maxHeight: '400px',
  overflowY: 'auto',
  marginTop: '12px',
};

// 按钮组样式
const buttonGroupStyles: React.CSSProperties = {
  position: 'absolute',
  top: '16px',
  right: '16px',
  display: 'flex',
  gap: '8px',
};

// 按钮基础样式
const buttonBaseStyles: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  transition: 'all 0.2s ease',
  color: 'rgba(0, 0, 0, 0.7)',
};



interface AIResponseCardProps {
  className?: string;
}

const AIResponseCard: React.FC<AIResponseCardProps> = ({ className }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [userMessage, setUserMessage] = useState<string>('');
  const [assistantMessage, setAssistantMessage] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [workflowProgress, setWorkflowProgress] = useState<AIWorkflowProgressData | null>(null);
  const [processingStage, setProcessingStage] = useState<string>('');
  const [parsedResult, setParsedResult] = useState<ParsedMessage | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // TTS 功能
  const {
    speak,
    stopSession,
    isPlaying: isTTSPlaying,
    currentText: currentTTSText,
    error: ttsError,
    clearError: clearTTSError
  } = useTTS({
    voice: 'zh_female_shuangkuaisisi_moon_bigtts',
    onStart: (item) => {
      console.log(`开始播放语音: ${item.text}`);
    },
    onEnd: (item) => {
      console.log(`语音播放完成: ${item.text}`);
    },
    onError: (item, error) => {
      console.error(`语音播放错误: ${item.text}`, error);
    }
  });

  // 监听事件
  useEffect(() => {
    // 显示响应（用户消息）
    const showResponseUnsubscribe = eventBus.subscribe(
      AIEventType.SHOW_RESPONSE,
      (data: AIResponseData) => {
        if (data.metadata?.role === 'user') {
          setUserMessage(data.content);
          if (data.sessionId) {
            setCurrentSessionId(data.sessionId);
          }
          setIsVisible(true);
        }
      }
    );

    // 流式开始
    const streamStartUnsubscribe = eventBus.subscribe(
      AIEventType.STREAM_START,
      (data: AIStreamData) => {
        setIsStreaming(true);
        setAssistantMessage('');
        setParsedResult(null);
        if (data.sessionId) {
          setCurrentSessionId(data.sessionId);
        }
      }
    );

    // 流式块
    const streamChunkUnsubscribe = eventBus.subscribe(
      AIEventType.STREAM_CHUNK,
      (data: AIStreamData) => {
        if (data.sessionId === currentSessionId) {
          // 只更新assistantMessage，不进行解析
          setAssistantMessage(data.content);
        }
      }
    );

    // 流式结束
    const streamEndUnsubscribe = eventBus.subscribe(
      AIEventType.STREAM_END,
      (data: AIStreamData) => {
        setIsStreaming(false);
        if (data.sessionId === currentSessionId) {
          setAssistantMessage(data.content);
          // 尝试解析完整的JSON
          const result = parseCompleteMessage(data.content);
          if (result.isComplete) {
            setParsedResult(result);
            // 自动播放AI响应内容
            handleAutoPlayTTS(result, data.sessionId);
          }
        }
      }
    );

    // 工作流进度
    const workflowProgressUnsubscribe = eventBus.subscribe(
      AIEventType.WORKFLOW_PROGRESS,
      (data: AIWorkflowProgressData) => {
        if (data.sessionId === currentSessionId) {
          setWorkflowProgress(data);
        }
      }
    );

    // 处理阶段
    const processingStageUnsubscribe = eventBus.subscribe(
      AIEventType.PROCESSING_STAGE,
      (data: AIProcessingStageData) => {
        if (data.sessionId === currentSessionId) {
          setProcessingStage(data.nodeTitle || '');
        }
      }
    );

    // 隐藏响应
    const hideResponseUnsubscribe = eventBus.subscribe(
      AIEventType.HIDE_RESPONSE,
      () => {
        setIsVisible(false);
      }
    );

    // 加载状态
    const loadingUnsubscribe = eventBus.subscribe(
      AIEventType.LOADING,
      (isLoading: boolean) => {
        setIsLoading(isLoading);
      }
    );

    // 清除响应
    const clearUnsubscribe = eventBus.subscribe(
      AIEventType.CLEAR,
      () => {
        setUserMessage('');
        setAssistantMessage('');
        setWorkflowProgress(null);
        setProcessingStage('');
        setParsedResult(null);
      }
    );

    // 全屏切换
    const fullscreenUnsubscribe = eventBus.subscribe(
      AIEventType.FULLSCREEN,
      () => {
        setIsFullscreen(true);
      }
    );

    // 最小化
    const minimizeUnsubscribe = eventBus.subscribe(
      AIEventType.MINIMIZE,
      () => {
        setIsFullscreen(false);
      }
    );

    // 错误处理
    const errorUnsubscribe = eventBus.subscribe(
      AIEventType.ERROR,
      (data) => {
        console.error('AI助手错误:', data.message);
        // 可以在这里添加错误提示UI
      }
    );

    // 清理订阅
    return () => {
      showResponseUnsubscribe();
      streamStartUnsubscribe();
      streamChunkUnsubscribe();
      streamEndUnsubscribe();
      workflowProgressUnsubscribe();
      processingStageUnsubscribe();
      hideResponseUnsubscribe();
      loadingUnsubscribe();
      clearUnsubscribe();
      fullscreenUnsubscribe();
      minimizeUnsubscribe();
      errorUnsubscribe();
    };
  }, [currentSessionId]);

  // 内容更新时滚动到底部
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [assistantMessage, parsedResult]);

  // 自动播放TTS
  const handleAutoPlayTTS = useCallback(async (result: ParsedMessage, sessionId: string) => {
    try {
      const messageText = result.message || getMessageFromStr(assistantMessage).message;
      if (messageText && messageText.trim()) {
        console.log(`自动播放AI响应: 会话=${sessionId}, 文本="${messageText}"`);
        await speak(sessionId, messageText);
      }
    } catch (error) {
      console.error('自动播放TTS失败:', error);
    }
  }, [assistantMessage, speak]);

  // 手动播放/停止TTS
  const handleToggleTTS = async () => {
    if (!currentSessionId) return;

    try {
      if (isTTSPlaying) {
        // 停止播放
        stopSession(currentSessionId);
      } else {
        // 开始播放
        const messageText = parsedResult?.message || getMessageFromStr(assistantMessage).message;
        if (messageText && messageText.trim()) {
          await speak(currentSessionId, messageText);
        }
      }
    } catch (error) {
      console.error('TTS播放控制失败:', error);
    }
  };

  // 处理关闭
  const handleClose = () => {
    // 停止当前会话的TTS播放
    if (currentSessionId) {
      stopSession(currentSessionId);
    }
    
    setIsVisible(false);
    // 延迟清除内容，等动画完成
    setTimeout(() => {
      setUserMessage('');
      setAssistantMessage('');
      setWorkflowProgress(null);
      setProcessingStage('');
      setCurrentSessionId(null);
      setParsedResult(null);
      clearTTSError();
    }, 300);
  };

  // 处理全屏切换
  const handleFullscreenToggle = () => {
    setIsFullscreen(!isFullscreen);
  };

  // 渲染工作流进度
  const renderWorkflowProgress = () => {
    if (!workflowProgress && !processingStage) return null;

    const { status } = workflowProgress || {};
    
    const statusText = {
      running: '执行中',
      succeeded: '已完成',
      failed: '执行失败',
      stopped: '已停止',
    }[status || 'running'] || '处理中';

    // 处理阶段名称映射
    const processingStageNames: Record<string, string> = {
      intent_recognition: '意图识别',
      knowledge_retrieval: '知识检索',
      prompt_construction: '提示词构建',
      model_generation: '模型生成',
      completed: '已完成',
      llm: '大模型生成',
      knowledge: '知识检索',
    };

    const currentStage = processingStage ? processingStageNames[processingStage] || processingStage : statusText;

    return (
      <div style={{ marginBottom: '16px' }}>
        <LoadingText text={currentStage} />
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={`ai-response-card ${className || ''} ${isFullscreen ? 'fullscreen' : ''}`}
          style={{
            ...cardStyles,
            ...(isFullscreen
              ? { 
                  top: '0', 
                  left: '0', 
                  right: '0', 
                  bottom: '0', 
                  width: '100%', 
                  maxWidth: 'none', 
                  maxHeight: '100vh', 
                  borderRadius: '0',
                  background: `
                    linear-gradient(135deg, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0.2) 100%),
                    linear-gradient(45deg, rgba(74, 144, 226, 0.15) 0%, rgba(80, 200, 120, 0.15) 50%, rgba(255, 107, 129, 0.15) 100%)
                  `
                }
              : {})
          }}
          initial={{ opacity: 0, y: -20, height: 'auto' }}
          animate={{ 
            opacity: 1, 
            y: 0,
            height: 'auto',
            transition: { 
              type: 'spring',
              stiffness: 300,
              damping: 30
            }
          }}
          exit={{ 
            opacity: 0, 
            y: -20,
            transition: { 
              duration: 0.2 
            }
          }}
        >
          {/* 用户问题 - 卡片内顶部 */}
          {userMessage && (
            <motion.div
              style={userQuestionStyles}
              initial={{ opacity: 0, y: -10 }}
              animate={{ 
                opacity: 1, 
                y: 0,
                transition: { 
                  duration: 0.3,
                  ease: "easeOut"
                }
              }}
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
            >
              {userMessage}
            </motion.div>
          )}

          {/* 按钮组 */}
              <div style={buttonGroupStyles}>
                {/* TTS播放/停止按钮 */}
                <motion.button
                  style={{
                    ...buttonBaseStyles,
                    opacity: (!parsedResult?.message && !getMessageFromStr(assistantMessage).message) ? 0.5 : 1
                  }}
                  whileHover={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    scale: 1.05,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleToggleTTS}
                  disabled={!parsedResult?.message && !getMessageFromStr(assistantMessage).message}
                  aria-label={isTTSPlaying ? '停止播放' : '播放语音'}
                  title={isTTSPlaying ? '停止语音播放' : '播放AI响应语音'}
                >
                  {isTTSPlaying ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="6" y="4" width="4" height="16" />
                      <rect x="14" y="4" width="4" height="16" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
                    </svg>
                  )}
                </motion.button>
                
                {/* 全屏/最小化按钮 */}
                <motion.button
                  style={buttonBaseStyles}
                  whileHover={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    scale: 1.05,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleFullscreenToggle}
                  aria-label={isFullscreen ? '最小化' : '全屏'}
                >
                  {isFullscreen ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                    </svg>
                  )}
                </motion.button>
                
                {/* 关闭按钮 */}
                <motion.button
                  style={buttonBaseStyles}
                  whileHover={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    scale: 1.05,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleClose}
                  aria-label="关闭"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </motion.button>
              </div>
              
              {/* 卡片内容 */}
              <motion.div 
                ref={contentRef}
                style={cardContentStyles}
              >
                
                
                {/* AI助手消息内容 */}
                <div className="message-content">
                  {/* 如果有解析结果，优先显示解析的message，否则显示原始内容 */}
                  {parsedResult?.message || getMessageFromStr(assistantMessage).message || '思考中...'}
                  {isStreaming && (
                    <span className="cursor-blink">|</span>
                  )}
                  
                  {/* TTS状态显示 */}
                  {isTTSPlaying && currentTTSText && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      backgroundColor: 'rgba(74, 144, 226, 0.1)',
                      borderRadius: '12px',
                      fontSize: '14px',
                      color: 'rgba(74, 144, 226, 0.8)',
                      border: '1px solid rgba(74, 144, 226, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: '#4A90E2',
                        animation: 'pulse 1.5s infinite'
                      }} />
                      <span>正在播放: {currentTTSText}</span>
                    </div>
                  )}
                  
                  {/* TTS错误显示 */}
                  {ttsError && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      backgroundColor: 'rgba(255, 107, 129, 0.1)',
                      borderRadius: '12px',
                      fontSize: '14px',
                      color: 'rgba(255, 107, 129, 0.8)',
                      border: '1px solid rgba(255, 107, 129, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span>语音播放失败: {ttsError}</span>
                      <button
                        onClick={clearTTSError}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'rgba(255, 107, 129, 0.8)',
                          cursor: 'pointer',
                          padding: '2px',
                          borderRadius: '4px'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
                {/* 工作流进度 - 仅在加载时显示 */}
                {(workflowProgress || processingStage) && (isStreaming || !parsedResult?.isComplete) && renderWorkflowProgress()}
                {/* 渲染组件 - 使用容器限制高度 */}
                {parsedResult?.componentName && (
                  <div style={componentContainerStyles}>
                    <ChatComponentRenderer
                      componentName={parsedResult.componentName}
                      data={parsedResult.data}
                      isLoading={!parsedResult.isComplete}
                    />
                  </div>
                )}
                
                {/* 加载状态 */}
                {isLoading && !isStreaming && (
                  <motion.div 
                    className="ai-loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      padding: '16px' 
                    }}
                  >
                    <div className="ai-loading-dots">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#007bff',
                            margin: '0 4px',
                          }}
                          animate={{
                            scale: [1, 1.5, 1],
                            opacity: [0.3, 1, 0.3],
                          }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            delay: i * 0.2,
                          }}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AIResponseCard; 